import Tooltip from "@/Components/Tooltip/Tooltip";
import { rgba } from "@/shared/utils/color";
import { ExternalDriverStatusEnum } from "@/shared/utils/enum";
import { formatDate } from "@/shared/utils/helpers";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useTranslation } from "react-i18next";
import Icon, { IconsVariant } from "../Icon/Icon";

type DriverStatusStyle = {
  icon: IconsVariant;
  color: string;
  translationKey: string;
  pulse?: boolean;
};

const driverStatuses: Partial<
  Record<ExternalDriverStatusEnum, DriverStatusStyle>
> = {
  [ExternalDriverStatusEnum.ARRIVING_AT_RESTAURANT]: {
    icon: "scooter",
    color: "var(--text-tertiary)",
    translationKey: "externalDriverArriving",
  },
  [ExternalDriverStatusEnum.AT_RESTAURANT]: {
    icon: "utensils",
    color: "var(--orange)",
    translationKey: "externalDriverAtRestaurant",
    pulse: true,
  },
  [ExternalDriverStatusEnum.ON_ITS_WAY]: {
    icon: "biking",
    color: "var(--primary)",
    translationKey: "externalDriverOnItsWay",
  },
  [ExternalDriverStatusEnum.DELIVERED]: {
    icon: "checkDouble",
    color: "var(--success)",
    translationKey: "externalDriverDelivered",
  },
};

const OrderExternalDriverStatus = ({ order }: { order: OrderV2 }) => {
  const { t } = useTranslation("", { keyPrefix: "order" });
  const {
    external_driver_status: status,
    external_driver_status_timestamp: timestamp,
  } = order.order_information;

  const driverStatus =
    status === undefined ? undefined : driverStatuses[status];
  if (!driverStatus) return null;

  const { icon, color, translationKey, pulse } = driverStatus;
  const label = t(translationKey);
  const time = timestamp ? formatDate(timestamp, "time") : null;

  return (
    <Tooltip
      content={
        timestamp ? `${label} · ${formatDate(timestamp, "dateTime")}` : label
      }
    >
      <div
        className="inline-flex max-w-full items-center gap-1 rounded-full py-[0.2rem] pl-[0.2rem] pr-[0.7rem]"
        style={{
          background: rgba(color, 0.14),
          boxShadow: `inset 0 0 0 0.1rem ${rgba(color, 0.32)}`,
        }}
      >
        <span
          className="relative flex size-[1.8rem] shrink-0 items-center justify-center rounded-full"
          style={{ background: color }}
        >
          {pulse && (
            <span
              className="absolute inset-0 animate-ping rounded-full"
              style={{ background: rgba(color, 0.5) }}
            />
          )}
          <Icon
            variants={icon}
            className="relative h-[0.95rem] w-[0.95rem] fill-white"
          />
        </span>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[1.15rem] font-bold leading-none text-dark">
          {label}
        </span>
        {time && (
          <span className="shrink-0 text-[1.05rem] font-semibold leading-none text-text-tertiary">
            {formatDate(timestamp, "time")}
          </span>
        )}
      </div>
    </Tooltip>
  );
};

export default OrderExternalDriverStatus;
